import { useEffect, useState, useCallback } from "react";
import {
  DollarSign, Star, Eye, EyeOff, ArrowUp, ArrowDown, Trash2, Plus,
  Save, X, History, Pencil, BarChart3, Tag, ListChecks,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const EMPTY_PKG = {
  id: "", name: "", tagline: "", price_from: 0, duration: "", best_for: "",
  highlights: [], featured: false, active: true,
};
const EMPTY_ADDON = { id: "", name: "", price: 0, active: true };

function Banner({ kind, msg, onClose }) {
  if (!msg) return null;
  const styles = kind === "error"
    ? "bg-red-50 border-red-200 text-red-700"
    : "bg-forest/8 border-forest/20 text-forest";
  return (
    <div className={`text-sm border px-4 py-3 rounded-xl flex items-center justify-between gap-3 ${styles}`}>
      <span>{msg}</span>
      {onClose && <button onClick={onClose} className="opacity-70 hover:opacity-100"><X size={14} /></button>}
    </div>
  );
}

function HistoryModal({ pkgId, onClose }) {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/admin/packages/${pkgId}/history`)
      .then((r) => setItems(r.data.items || []))
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || e.message));
  }, [pkgId]);
  return (
    <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-[70] flex items-center justify-center p-4" data-testid="history-modal">
      <div className="card-surface p-8 max-w-lg w-full max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="eyebrow">Price history</div>
            <h3 className="font-heading text-xl tracking-tight">{pkgId}</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-bone rounded-full" aria-label="Close"><X size={16} /></button>
        </div>
        {err && <div className="text-sm text-red-700 mb-3">{err}</div>}
        {items.length === 0 ? (
          <p className="text-sm text-muted2">No price changes recorded yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-bone/60">
              <tr className="text-left">
                <th className="px-3 py-2 eyebrow">When</th>
                <th className="px-3 py-2 eyebrow">From</th>
                <th className="px-3 py-2 eyebrow">To</th>
                <th className="px-3 py-2 eyebrow">Δ</th>
                <th className="px-3 py-2 eyebrow">By</th>
              </tr>
            </thead>
            <tbody>
              {items.map((h) => (
                <tr key={h.id} className="border-t border-black/5">
                  <td className="px-3 py-2 text-xs text-muted2">{new Date(h.changed_at).toLocaleString()}</td>
                  <td className="px-3 py-2">${h.old_price}</td>
                  <td className="px-3 py-2 font-medium">${h.new_price}</td>
                  <td className={`px-3 py-2 ${h.delta >= 0 ? "text-forest" : "text-red-700"}`}>
                    {h.delta >= 0 ? "+" : ""}{h.delta}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted2">{h.actor_email}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function PackageEditor({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial);
  const isNew = !initial.id;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const setHighlight = (i, v) => {
    const next = [...form.highlights]; next[i] = v; setForm({ ...form, highlights: next });
  };
  const addHighlight = () => setForm({ ...form, highlights: [...form.highlights, ""] });
  const removeHighlight = (i) => setForm({ ...form, highlights: form.highlights.filter((_, idx) => idx !== i) });

  const submit = () => {
    onSave({
      ...form,
      price_from: Number(form.price_from) || 0,
      highlights: form.highlights.filter((h) => h.trim().length > 0),
    });
  };

  return (
    <div className="card-surface p-6 space-y-4" data-testid="package-editor">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-xl tracking-tight">{isNew ? "New package" : `Edit · ${initial.name}`}</h3>
        <button onClick={onCancel} className="p-2 hover:bg-bone rounded-full"><X size={16} /></button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="eyebrow block mb-1">Name</label>
          <input className="input-base" value={form.name} onChange={set("name")} data-testid="editor-name" />
        </div>
        <div>
          <label className="eyebrow block mb-1">Price (USD)</label>
          <input className="input-base font-heading text-lg" type="number" min="0" value={form.price_from} onChange={set("price_from")} data-testid="editor-price" />
        </div>
        <div className="md:col-span-2">
          <label className="eyebrow block mb-1">Tagline</label>
          <input className="input-base" value={form.tagline} onChange={set("tagline")} data-testid="editor-tagline" />
        </div>
        <div>
          <label className="eyebrow block mb-1">Duration</label>
          <input className="input-base" value={form.duration} onChange={set("duration")} placeholder="e.g. 3-5 hours" />
        </div>
        <div>
          <label className="eyebrow block mb-1">Best for</label>
          <input className="input-base" value={form.best_for} onChange={set("best_for")} placeholder="Who this is right for" />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="eyebrow flex items-center gap-2"><ListChecks size={12} /> Highlights</label>
          <button onClick={addHighlight} className="text-xs text-forest hover:underline inline-flex items-center gap-1"><Plus size={12} /> Add</button>
        </div>
        <div className="space-y-2">
          {form.highlights.map((h, i) => (
            <div key={i} className="flex gap-2">
              <input
                className="input-base flex-1"
                value={h}
                onChange={(e) => setHighlight(i, e.target.value)}
                data-testid={`editor-highlight-${i}`}
              />
              <button onClick={() => removeHighlight(i)} className="p-2 text-red-700 hover:bg-red-50 rounded-lg">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-6 pt-2">
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.featured} onChange={set("featured")} data-testid="editor-featured" />
          <span className="text-sm">Featured (Most Popular)</span>
        </label>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.active} onChange={set("active")} data-testid="editor-active" />
          <span className="text-sm">Active (visible to customers)</span>
        </label>
      </div>

      <div className="flex gap-3 pt-2">
        <button onClick={submit} disabled={saving || !form.name || !form.price_from} className="btn-primary disabled:opacity-50" data-testid="editor-save">
          <Save size={14} /> {saving ? "Saving…" : isNew ? "Create package" : "Save changes"}
        </button>
        <button onClick={onCancel} className="btn-ghost" data-testid="editor-cancel">Cancel</button>
      </div>
    </div>
  );
}

function AddOnEditor({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial);
  const isNew = !initial.id;
  return (
    <div className="card-surface p-5" data-testid="addon-editor">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading text-base tracking-tight">{isNew ? "New add-on" : "Edit add-on"}</h3>
        <button onClick={onCancel} className="p-1 hover:bg-bone rounded-full"><X size={14} /></button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input className="input-base" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="addon-name" />
        <input className="input-base font-heading" type="number" min="0" placeholder="Price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="addon-price" />
      </div>
      <label className="inline-flex items-center gap-2 mt-3 cursor-pointer">
        <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
        <span className="text-sm">Active</span>
      </label>
      <div className="flex gap-2 mt-4">
        <button
          onClick={() => onSave({ ...form, price: Number(form.price) || 0 })}
          disabled={saving || !form.name || form.price === ""}
          className="btn-primary text-sm disabled:opacity-50"
          data-testid="addon-save"
        >
          <Save size={12} /> {saving ? "Saving…" : "Save"}
        </button>
        <button onClick={onCancel} className="btn-ghost text-sm">Cancel</button>
      </div>
    </div>
  );
}

export default function PricingPanel() {
  const [data, setData] = useState({ packages: [], add_ons: [] });
  const [editingPkg, setEditingPkg] = useState(null);
  const [editingAddon, setEditingAddon] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/catalog");
      setData(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 2500); };

  // Package actions
  const savePackage = async (form) => {
    setSaving(true); setErr("");
    try {
      if (form.id && data.packages.find((p) => p.id === form.id)) {
        const { id, ...payload } = form;
        await api.patch(`/admin/packages/${id}`, payload);
        flash(`Saved · ${form.name}`);
      } else {
        await api.post("/admin/packages", form);
        flash("Package created");
      }
      setEditingPkg(null);
      load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const deletePackage = async (id) => {
    if (!window.confirm(`Delete package "${id}"? If any bookings reference it, it'll be soft-deleted (hidden) to preserve history.`)) return;
    try {
      const { data: r } = await api.delete(`/admin/packages/${id}`);
      flash(r.deactivated ? "Soft-deleted (kept for history)" : "Deleted");
      load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const movePackage = async (id, dir) => {
    const ids = [...data.packages].sort((a, b) => a.sort_order - b.sort_order).map((p) => p.id);
    const i = ids.indexOf(id);
    const j = dir === "up" ? i - 1 : i + 1;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    await api.post("/admin/packages/reorder", { ordered_ids: ids });
    load();
  };

  const togglePkgActive = async (pkg) => {
    setSaving(true);
    try {
      const { id, stats, sort_order, created_at, updated_at, ...payload } = pkg;
      await api.patch(`/admin/packages/${pkg.id}`, { ...payload, active: !pkg.active });
      load();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const togglePkgFeatured = async (pkg) => {
    setSaving(true);
    try {
      const { id, stats, sort_order, created_at, updated_at, ...payload } = pkg;
      await api.patch(`/admin/packages/${pkg.id}`, { ...payload, featured: !pkg.featured });
      load();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  // Add-on actions
  const saveAddon = async (form) => {
    setSaving(true); setErr("");
    try {
      if (form.id && data.add_ons.find((a) => a.id === form.id)) {
        const { id, ...payload } = form;
        await api.patch(`/admin/addons/${id}`, payload);
        flash(`Saved · ${form.name}`);
      } else {
        await api.post("/admin/addons", form);
        flash("Add-on created");
      }
      setEditingAddon(null);
      load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const deleteAddon = async (id) => {
    if (!window.confirm(`Delete add-on "${id}"?`)) return;
    try {
      const { data: r } = await api.delete(`/admin/addons/${id}`);
      flash(r.deactivated ? "Soft-deleted (kept for history)" : "Deleted");
      load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const moveAddon = async (id, dir) => {
    const ids = [...data.add_ons].sort((a, b) => a.sort_order - b.sort_order).map((a) => a.id);
    const i = ids.indexOf(id);
    const j = dir === "up" ? i - 1 : i + 1;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    await api.post("/admin/addons/reorder", { ordered_ids: ids });
    load();
  };

  const sortedPkgs = [...data.packages].sort((a, b) => a.sort_order - b.sort_order);
  const sortedAddons = [...data.add_ons].sort((a, b) => a.sort_order - b.sort_order);

  return (
    <div className="space-y-8" data-testid="pricing-panel">
      <Banner kind="error" msg={err} onClose={() => setErr("")} />
      <Banner kind="success" msg={msg} onClose={() => setMsg("")} />

      {/* Packages */}
      <section>
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="eyebrow">Service packages</div>
            <h2 className="font-heading text-2xl tracking-tight">{sortedPkgs.length} active packages</h2>
          </div>
          {!editingPkg && (
            <button onClick={() => setEditingPkg(EMPTY_PKG)} className="btn-primary text-sm" data-testid="new-package-btn">
              <Plus size={14} /> New package
            </button>
          )}
        </div>

        {editingPkg && (
          <div className="mb-6">
            <PackageEditor
              initial={editingPkg}
              onSave={savePackage}
              onCancel={() => setEditingPkg(null)}
              saving={saving}
            />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {sortedPkgs.map((pkg, idx) => (
            <div
              key={pkg.id}
              className={`card-surface p-6 ${!pkg.active ? "opacity-60" : ""} ${pkg.featured ? "ring-2 ring-clay/40" : ""}`}
              data-testid={`pkg-row-${pkg.id}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Tag size={14} className="text-forest" />
                    <h3 className="font-heading text-xl tracking-tight">{pkg.name}</h3>
                    {pkg.featured && <span className="text-[10px] uppercase tracking-[0.18em] bg-clay text-white px-2 py-0.5 rounded-full">Featured</span>}
                    {!pkg.active && <span className="text-[10px] uppercase tracking-[0.18em] bg-ink/10 text-ink/70 px-2 py-0.5 rounded-full">Inactive</span>}
                  </div>
                  <p className="text-xs text-muted2 mt-1">{pkg.tagline}</p>
                  <p className="text-[10px] text-muted2 mt-1 font-mono">{pkg.id}</p>
                </div>
                <div className="text-right">
                  <div className="font-heading text-3xl text-ink leading-none">${pkg.price_from}</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted2 mt-1">{pkg.duration}</div>
                </div>
              </div>

              {/* Stats */}
              {pkg.stats && (
                <div className="grid grid-cols-2 gap-2 my-4 bg-bone/60 border border-black/5 rounded-xl p-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted2 flex items-center gap-1"><BarChart3 size={10} /> Bookings</div>
                    <div className="font-heading text-xl">{pkg.stats.bookings_count}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted2 flex items-center gap-1"><DollarSign size={10} /> Revenue</div>
                    <div className="font-heading text-xl">${pkg.stats.revenue.toLocaleString()}</div>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-1 pt-2 border-t border-black/5">
                <button onClick={() => setEditingPkg(pkg)} className="btn-ghost text-xs" data-testid={`pkg-edit-${pkg.id}`}>
                  <Pencil size={12} /> Edit
                </button>
                <button onClick={() => togglePkgFeatured(pkg)} className="btn-ghost text-xs" title="Toggle featured" data-testid={`pkg-featured-${pkg.id}`}>
                  <Star size={12} className={pkg.featured ? "text-clay fill-clay" : ""} />
                </button>
                <button onClick={() => togglePkgActive(pkg)} className="btn-ghost text-xs" title="Toggle active" data-testid={`pkg-active-${pkg.id}`}>
                  {pkg.active ? <Eye size={12} /> : <EyeOff size={12} />}
                </button>
                <button onClick={() => setHistoryFor(pkg.id)} className="btn-ghost text-xs" title="Price history" data-testid={`pkg-history-${pkg.id}`}>
                  <History size={12} />
                </button>
                <div className="flex items-center gap-0.5 ml-1">
                  <button onClick={() => movePackage(pkg.id, "up")} disabled={idx === 0} className="btn-ghost text-xs disabled:opacity-30 px-2" data-testid={`pkg-up-${pkg.id}`}>
                    <ArrowUp size={12} />
                  </button>
                  <button onClick={() => movePackage(pkg.id, "down")} disabled={idx === sortedPkgs.length - 1} className="btn-ghost text-xs disabled:opacity-30 px-2" data-testid={`pkg-down-${pkg.id}`}>
                    <ArrowDown size={12} />
                  </button>
                </div>
                <button onClick={() => deletePackage(pkg.id)} className="btn-ghost text-xs text-red-700 ml-auto" data-testid={`pkg-delete-${pkg.id}`}>
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Add-ons */}
      <section>
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="eyebrow">Add-ons</div>
            <h2 className="font-heading text-2xl tracking-tight">{sortedAddons.length} add-on services</h2>
          </div>
          {!editingAddon && (
            <button onClick={() => setEditingAddon(EMPTY_ADDON)} className="btn-primary text-sm" data-testid="new-addon-btn">
              <Plus size={14} /> New add-on
            </button>
          )}
        </div>

        {editingAddon && (
          <div className="mb-4 max-w-xl">
            <AddOnEditor
              initial={editingAddon}
              onSave={saveAddon}
              onCancel={() => setEditingAddon(null)}
              saving={saving}
            />
          </div>
        )}

        <div className="card-surface overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-bone/60">
              <tr className="text-left">
                <th className="px-5 py-3 eyebrow">Add-on</th>
                <th className="px-5 py-3 eyebrow">Price</th>
                <th className="px-5 py-3 eyebrow">Status</th>
                <th className="px-5 py-3 eyebrow text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedAddons.length === 0 && (
                <tr><td colSpan="4" className="px-5 py-10 text-center text-muted2">No add-ons yet.</td></tr>
              )}
              {sortedAddons.map((a, idx) => (
                <tr key={a.id} className={`border-t border-black/5 ${!a.active ? "opacity-60" : ""}`} data-testid={`addon-row-${a.id}`}>
                  <td className="px-5 py-4">
                    <div className="font-medium">{a.name}</div>
                    <div className="text-[10px] text-muted2 font-mono">{a.id}</div>
                  </td>
                  <td className="px-5 py-4 font-heading text-lg">${a.price}</td>
                  <td className="px-5 py-4">
                    <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full ${a.active ? "bg-forest/10 text-forest" : "bg-ink/10 text-ink/70"}`}>
                      {a.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="inline-flex items-center gap-0.5">
                      <button onClick={() => moveAddon(a.id, "up")} disabled={idx === 0} className="btn-ghost text-xs disabled:opacity-30 px-2"><ArrowUp size={12} /></button>
                      <button onClick={() => moveAddon(a.id, "down")} disabled={idx === sortedAddons.length - 1} className="btn-ghost text-xs disabled:opacity-30 px-2"><ArrowDown size={12} /></button>
                      <button onClick={() => setEditingAddon(a)} className="btn-ghost text-xs" data-testid={`addon-edit-${a.id}`}><Pencil size={12} /></button>
                      <button onClick={() => deleteAddon(a.id)} className="btn-ghost text-xs text-red-700" data-testid={`addon-delete-${a.id}`}><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {historyFor && <HistoryModal pkgId={historyFor} onClose={() => setHistoryFor(null)} />}
    </div>
  );
}
